import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";
import BusinessCard from "@/components/BusinessCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Umbrella } from "lucide-react";

export default function Browse() {
  usePageMeta("Browse businesses", "Browse vetted Black-owned contractors and businesses by trade and service area across the GTA and Canada.");
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState({ trades: [], service_areas: [] });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const trade = params.get("trade") || "all";
  const city = params.get("city") || "all";

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (trade !== "all") q.trade = trade;
    if (city !== "all") q.city = city;
    api.get("/businesses", { params: q }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [trade, city]);

  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === "all") next.delete(k); else next.set(k, v);
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-950">Vetted Black-owned businesses</h1>
      <p className="mt-2 text-slate-500">Filter by trade and service area across the GTA and Canada.</p>

      <div className="mt-6 flex flex-wrap gap-3">
        <Select value={trade} onValueChange={(v) => update("trade", v)}>
          <SelectTrigger data-testid="filter-trade-select" className="w-52 rounded-full"><SelectValue placeholder="All trades" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All trades</SelectItem>
            {meta.trades.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={city} onValueChange={(v) => update("city", v)}>
          <SelectTrigger data-testid="filter-city-select" className="w-52 rounded-full"><SelectValue placeholder="All areas" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All service areas</SelectItem>
            {meta.service_areas.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="mt-12 text-slate-400">Loading…</p>
      ) : items.length === 0 ? (
        <div className="mt-16 text-center text-slate-400">
          <Umbrella size={40} className="mx-auto mb-3" />
          <p>No businesses match yet. Try clearing filters.</p>
          <Link to="/for-businesses" data-testid="empty-join-link" className="mt-4 inline-block text-sm font-semibold text-amber-700 hover:underline">Are you in this trade? Join Black Umbrella</Link>
          <p className="mt-2 text-sm text-slate-500" data-testid="empty-post-job-line">
            <Link to={`/post-job${trade !== "all" ? `?trade=${encodeURIComponent(trade)}` : ""}`} data-testid="empty-post-job-link" className="font-semibold text-slate-700 hover:text-amber-700 hover:underline">Post a job anyway</Link> — we will notify you when a matching business joins.
          </p>
        </div>
      ) : (
        <div data-testid="business-grid" className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((b) => <BusinessCard key={b.id} b={b} />)}
        </div>
      )}
    </div>
  );
}
